/**
 * Image URL Cache
 * 
 * Caches presigned S3 URLs to reduce API calls.
 * Does NOT download/store image blobs (avoids CORS issues).
 * 
 * Presigned URLs are valid for 1 hour, we cache them for 50 minutes.
 */

interface CachedUrl {
  url: string;
  timestamp: number;
  photoId: string;
  size: 'small' | 'medium' | 'large';
  accessCount: number;
  lastAccessed: number;
}

export interface CacheStatsDisplay {
  itemCount: number;
  cacheHits: number;
  cacheMisses: number;
  hitRate: number;
  formattedSize: string;
}

class ImageCache {
  private readonly STORAGE_KEY = 'family-album-url-cache';
  private readonly STATS_KEY = 'family-album-cache-stats';
  private readonly MAX_AGE = 50 * 60 * 1000; // 50 minutes (URLs valid for 60)
  private readonly MAX_ENTRIES = 1000; // Limit cache size
  private cache: Map<string, CachedUrl> = new Map();
  private stats = {
    cacheHits: 0,
    cacheMisses: 0,
  };

  constructor() {
    this.loadFromStorage();
  }

  private getCacheKey(photoId: string, size: 'small' | 'medium' | 'large'): string {
    return `${photoId}-${size}`;
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const storedStats = localStorage.getItem(this.STATS_KEY);

      if (stored) {
        const entries: CachedUrl[] = JSON.parse(stored);
        const now = Date.now();

        // Only load non-expired entries
        entries.forEach((entry) => {
          if (now - entry.timestamp < this.MAX_AGE) {
            const key = this.getCacheKey(entry.photoId, entry.size);
            this.cache.set(key, entry);
          }
        });

        console.log(`📦 Loaded ${this.cache.size} cached URLs from storage`);
      }

      if (storedStats) {
        this.stats = JSON.parse(storedStats);
      }
    } catch (error) {
      console.error('Failed to load cache from storage:', error);
      this.cache.clear();
    }
  }

  private saveToStorage() {
    try {
      const entries = Array.from(this.cache.values());
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(entries));
      localStorage.setItem(this.STATS_KEY, JSON.stringify(this.stats));
    } catch (error) {
      console.error('Failed to save cache to storage:', error);
      // If storage is full, clear it and try again
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.clear();
      }
    }
  }

  /**
   * Get cached URL for an image
   * Returns null if not cached or expired
   */
  async get(photoId: string, size: 'small' | 'medium' | 'large'): Promise<string | null> {
    const key = this.getCacheKey(photoId, size);
    const cached = this.cache.get(key);

    if (!cached) {
      this.stats.cacheMisses++;
      return null;
    }

    // Check if expired (50 minutes)
    const age = Date.now() - cached.timestamp;
    if (age > this.MAX_AGE) {
      console.log(`⏰ Cache expired for ${key} (${Math.round(age / 60000)} min old)`);
      this.cache.delete(key);
      this.stats.cacheMisses++;
      this.saveToStorage();
      return null;
    }

    // Update access stats
    cached.accessCount++;
    cached.lastAccessed = Date.now();
    this.stats.cacheHits++;

    console.log(`✅ Cache hit: ${key} (${Math.round(age / 60000)} min old)`);
    return cached.url;
  }

  /**
   * Cache a presigned URL
   */
  async set(photoId: string, size: 'small' | 'medium' | 'large', url: string): Promise<void> {
    const key = this.getCacheKey(photoId, size);

    // Check cache size limit
    if (this.cache.size >= this.MAX_ENTRIES) {
      console.log('🧹 Cache full, evicting oldest entries...');
      this.evictOldest(Math.floor(this.MAX_ENTRIES * 0.1)); // Remove 10%
    }

    this.cache.set(key, {
      url,
      photoId,
      size,
      timestamp: Date.now(),
      accessCount: 0,
      lastAccessed: Date.now(),
    });

    this.saveToStorage();
    console.log(`💾 Cached URL: ${key}`);
  }

  /**
   * Evict oldest/least accessed entries
   */
  private evictOldest(count: number) {
    const entries = Array.from(this.cache.entries());

    // Sort by last accessed (oldest first)
    entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);

    // Remove oldest entries
    for (let i = 0; i < count && i < entries.length; i++) {
      this.cache.delete(entries[i][0]);
    }

    console.log(`🗑️  Evicted ${count} entries`);
    this.saveToStorage();
  }

  /**
   * Delete specific entry
   */
  async delete(photoId: string, size: 'small' | 'medium' | 'large'): Promise<void> {
    const key = this.getCacheKey(photoId, size);
    this.cache.delete(key);
    this.saveToStorage();
  }

  /**
   * Clear all cached URLs
   */
  async clear(): Promise<void> {
    this.cache.clear();
    this.stats.cacheHits = 0;
    this.stats.cacheMisses = 0;
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STATS_KEY);
    console.log('🧹 Cache cleared');
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<CacheStatsDisplay> {
    const totalRequests = this.stats.cacheHits + this.stats.cacheMisses;
    const hitRate = totalRequests > 0 ? (this.stats.cacheHits / totalRequests) * 100 : 0;

    // Find oldest entry
    let oldestTimestamp: number | null = null;
    this.cache.forEach((entry) => {
      if (oldestTimestamp === null || entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
      }
    });

    const oldestAge = oldestTimestamp ? Date.now() - oldestTimestamp : null;

    return {
      itemCount: this.cache.size,
      cacheHits: this.stats.cacheHits,
      cacheMisses: this.stats.cacheMisses,
      hitRate,
      formattedSize: `${this.cache.size} URLs (oldest: ${oldestAge ? Math.round(oldestAge / 60000) + ' min' : 'N/A'})`,
    };
  }

  /**
   * Clean up expired entries (called periodically)
   */
  async cleanup(): Promise<void> {
    const now = Date.now();
    let removed = 0;

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > this.MAX_AGE) {
        this.cache.delete(key);
        removed++;
      }
    });

    if (removed > 0) {
      console.log(`🧹 Cleaned up ${removed} expired URLs`);
      this.saveToStorage();
    }
  }
}

// Singleton instance
export const imageCache = new ImageCache();

// Periodic cleanup (every 10 minutes)
setInterval(() => {
  imageCache.cleanup();
}, 10 * 60 * 1000);
